<script setup lang="ts">
import { watch } from "vue";
// shared
import HeartLogo from "@/shared/components/atomic/svgs/heart-logo/HeartLogo.vue";
import Logout from "@/shared/components/atomic/svgs/logout/Logout.vue";
import useRefetchTriggerStore from "@/shared/models/refetch-trigger/store";
import AlertReconfirmEvent from "@/shared/components/popup/AlertReconfirmEvent.vue";
import { usePopupManager } from "@/shared/composables/usePopupManager";
import CommonTriggerPossessPopup from "@/shared/components/wrapper/CommonTriggerPossessPopup.vue";
import { useQueryErrorCapture } from "@/shared/composables/useQueryErrorCapture";

// features
import TriggerPendingRegistrationAlert from "@/features/pending-registration/ui/TriggerPendingRegistrationAlert.vue";
import PendingRegistrationUserList from "@/features/pending-registration/ui/PendingRegistrationUserList.vue";
import { useMutationAttemptLogout } from "@/features/authentication/services/query/useMutationAttemptLogout";

//widgets
import { NAVIGATION_LINKS_IN_PRINCIPAL } from "@/widgets/navigation/config/const";
import { useGetPendingRegistrationUserList } from "@/widgets/navigation/services/query/useGetPendingRegistrationUserList";
import NavigationLink from "@/widgets/navigation/ui/NavigationLink.vue";
import AcademyNameCard from "@/widgets/navigation/ui/AcademyNameCard.vue";

const { capture } = useQueryErrorCapture();
const refetchTriggerStore = useRefetchTriggerStore();
const { openPopup } = usePopupManager();
const { attemptLogout, isPending } = useMutationAttemptLogout();
const {
    convertedPendingRegistrationUserList,
    isFetchingNextPage,
    fetchNextPage,
    error,
    isFetchNextPageError,
    totalCount,
} = useGetPendingRegistrationUserList();

/**
 * @description 각 Link 접근 시 refetch 여부를 결정하기위한 state 변경값을 store 로 전달합니다.
 */
const emitRefetchTrigger = (trigger: "routerPush" | "resetPath") => {
    refetchTriggerStore.determineExecuteTrigger(trigger);
};

/**
 * @description 로그아웃 팝업 오픈
 */
const openLogoutPopup = () => {
    openPopup({
        component: AlertReconfirmEvent,
        props: {
            closeKey: "",
            rest: {
                title: "정말 로그아웃을 하시겠습니까?",
                subTitle: "로그아웃",
                confirmButtonName: "로그아웃",
                cancelButtonName: "취소",
                confirmCallback: attemptLogout,
                confirmCallbackPending: isPending.value,
            },
        },
    });
};

watch([error, isFetchNextPageError], () => {
    if (error.value) {
        capture(error.value);
    }
});
</script>

<template>
    <header
        class="flex sticky top-0 justify-center items-center px-[3.2rem] py-[1.2rem] w-full bg-white z-100 border-b border-border-gray"
    >
        <div class="flex justify-between items-center w-full">
            <div class="flex justify-start items-center w-fit">
                <div class="flex gap-7 justify-center items-center">
                    <div class="flex justify-center items-center px-small">
                        <HeartLogo class="size-[3.6rem]" />
                    </div>
                    <div class="flex gap-3 justify-center items-center">
                        <NavigationLink
                            v-for="navigation in NAVIGATION_LINKS_IN_PRINCIPAL"
                            :key="navigation.index"
                            :index="navigation.index"
                            :text="navigation.text"
                            :path="navigation.path"
                            @emit-refetch-trigger="emitRefetchTrigger"
                        />
                    </div>
                </div>
            </div>
            <div class="flex gap-5 justify-end items-center w-fit">
                <CommonTriggerPossessPopup v-if="convertedPendingRegistrationUserList?.length > 0">
                    <template #trigger>
                        <TriggerPendingRegistrationAlert
                            :pendingRegistrationUserCount="totalCount"
                        />
                    </template>
                    <template #modal>
                        <PendingRegistrationUserList
                            :users="convertedPendingRegistrationUserList"
                            :nextPageRequest="fetchNextPage"
                            :isNextPageLoading="isFetchingNextPage"
                        />
                    </template>
                </CommonTriggerPossessPopup>

                <AcademyNameCard />

                <div
                    class="flex gap-1 justify-center items-center h-full cursor-pointer"
                    @click="openLogoutPopup"
                >
                    <p class="font-normal text-t-basic text-body-small font-pretendard">로그아웃</p>
                    <div class="size-[1.6rem]">
                        <Logout class="text-icon-gray" />
                    </div>
                </div>
            </div>
        </div>
    </header>
</template>
